package dev.danko.roblox_craft_ai.uikit.components

import kotlinx.browser.document
import kotlinx.browser.window
import kotlin.js.Date
import kotlin.math.max

private fun nowMs(): Long = Date.now().toLong()

private const val MONACO_VERSION = "0.45.0"
private const val MONACO_STYLESHEET_ID = "monaco-editor-stylesheet"

private fun ensureMonacoStylesheet() {
    if (document.getElementById(MONACO_STYLESHEET_ID) != null) return
    val link = document.createElement("link")
    link.id = MONACO_STYLESHEET_ID
    link.setAttribute("rel", "stylesheet")
    link.setAttribute("href", "https://cdn.jsdelivr.net/npm/monaco-editor@$MONACO_VERSION/min/vs/editor/editor.main.css")
    (document.body ?: document.head)?.appendChild(link)
}

fun ensureMonacoLoaded(onLoaded: () -> Unit = {}, attempt: Int = 0, timeoutStartMs: Long = nowMs()) {
    val win = window.asDynamic()
    val loaded = (win.monaco != null && win.monaco?.editor != null) as? Boolean ?: false
    if (loaded) {
        onLoaded()
        return
    }

    val guard = win.__robloxMonacoStarted as? Boolean
    if (guard != true) {
        win.__robloxMonacoStarted = true
        ensureMonacoStylesheet()
        val blk = document.createElement("script").also { it.asDynamic().type = "module" }
        val bundleUrl = "https://esm.sh/monaco-editor@$MONACO_VERSION?bundle&deps=none"
        val workerUrl = "https://esm.sh/monaco-editor@$MONACO_VERSION/esm/vs/editor/editor.worker.js?bundle"
        // Monaco runs tokenising and diffing in a web worker; without one it does that on the main thread and
        // warns about UI freezes. A worker cannot start from another origin directly, so a same-origin blob
        // module imports the CDN worker. Luau has no language service, so the base editor worker covers all labels.
        val scriptBody = """
            if (!window.MonacoEnvironment) {
                window.MonacoEnvironment = {
                    getWorker: function () {
                        var src = "import '$workerUrl';";
                        var url = URL.createObjectURL(new Blob([src], { type: 'text/javascript' }));
                        return new Worker(url, { type: 'module' });
                    }
                };
            }
            import('$bundleUrl').then(function(m){ 
                window.monaco = m.monaco || m.default || m; 
                var luauKeywords = ['and','break','do','else','elseif','end','false','for','function','if','in','local','nil','not','or','repeat','return','then','true','until','while','continue','export','type','typeof'];
                var robloxGlobals = ['game','workspace','script','Instance','print','warn','error','pairs','ipairs','table','string','math','task','wait','Vector2','Vector3','UDim','UDim2','CFrame','Color3','BrickColor','Enum','TweenService','Players','ReplicatedStorage','ServerScriptService','Part','Model','Folder','Humanoid','require','pcall','tostring','tonumber','setmetatable','getmetatable','utf8'];
                window.monaco.languages.register({ id: 'luau' });
                window.monaco.languages.setMonarchTokensProvider('luau', {
                  keywords: luauKeywords,
                  robloxGlobals: robloxGlobals,
                  tokenizer: {
                    root: [
                      [/--\[\[[\s\S]*?\]\]/, 'comment.block'],
                      [/--.*$/, 'comment.line'],
                      [/"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'/, 'string'],
                      [/\[\[[\s\S]*?\]\]/, 'string'],
                      [/\b0x[0-9a-fA-F]+\b/, 'number'],
                      [/\b\d+(\.\d+)?([eE][+-]?\d+)?\b/, 'number'],
                      [/\b(local|function|end|if|then|else|elseif|for|in|do|while|repeat|until|return|break|true|false|nil|not|and|or|continue|type|typeof|export)\b/, 'keyword'],
                      [/\b(game|workspace|script|Instance|print|warn|error|pairs|ipairs|table|string|math|task|wait|Vector2|Vector3|UDim|UDim2|CFrame|Color3|Enum|Players|ReplicatedStorage|ServerScriptService|Part|Model|Folder|Humanoid|require|pcall|tostring|tonumber|setmetatable|getmetatable|utf8|TweenService)\b/, 'type.roblox'],
                      [/[a-zA-Z_][a-zA-Z0-9_]*/, 'identifier'],
                      [/[+\-*\/%=<>!&|^~#.,:;(){}[\]]/, 'delimiter']
                    ]
                  }
                });
                window.monaco.editor.defineTheme('robloxDark', {
                  base: 'vs-dark', inherit: true,
                  rules: [
                    { token: 'keyword', foreground: 'A855F7', fontStyle: 'bold' },
                    { token: 'string', foreground: '10B981' },
                    { token: 'number', foreground: '3B82F6' },
                    { token: 'type.roblox', foreground: 'F59E0B' },
                    { token: 'type', foreground: 'F59E0B' },
                    { token: 'comment', foreground: '9090A8', fontStyle: 'italic' },
                    { token: 'delimiter', foreground: '94A3B8' },
                    { token: 'identifier', foreground: 'F8FAFC' }
                  ],
                  colors: {
                    'editor.background': '#141420',
                    'editor.lineHighlightBackground': '#1A1A2E',
                    'editorGutter.background': '#141420',
                    'editorCursor.foreground': '#A855F7',
                    'editorLineNumber.foreground': '#9090A8',
                    'editorLineNumber.activeForeground': '#F8FAFC'
                  }
                });
                window.monaco.editor.setTheme('robloxDark');
                var e = new CustomEvent('monaco-loaded',{detail:{ok:true}}); window.dispatchEvent(e); 
            }).catch(function(err){ 
                console.warn('[Monaco] ESM load fail, fallback to CodeViewer.', err); 
                var f = new CustomEvent('monaco-failed',{detail:{err:String(err)}}); window.dispatchEvent(f); 
            });
        """.trimIndent()
        blk.asDynamic().textContent = scriptBody
        (document.body ?: document.head)?.appendChild(blk)
    }

    val elapsed = nowMs() - timeoutStartMs
    if (elapsed > 8000L) {
        console.warn("[Monaco] load timeout 8s, fallback to static CodeViewer")
        val fe = js("new CustomEvent('monaco-failed',{detail:{err:'timeout'}})")
        js("window.dispatchEvent(fe)")
        return
    }
    if (attempt < 80) {
        window.setTimeout({ ensureMonacoLoaded(onLoaded, attempt + 1, timeoutStartMs) }, 100)
    }
}
